import * as React from 'react';
import {get} from 'lodash';
import {Plugins, TypeImplementation} from '@packages/systems/wfdl';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import Button from '@packages/systems/design-system/Button';
import {
  Note,
  NoteLink,
  EditIcon,
  CloseDefaultIcon,
  ClockwiseIcon,
} from '@packages/systems/spring';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';

// We assume the `value` is in correct shape here and thus set it to any
const createLiteralExpr = (value: any) =>
  ELiteral({
    name: qualify(Plugins.name('Commerce'), typeName('MultiTextEdit')),
    value,
  });

const FlexColumn = styledDiv(({alignItems}) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems,
}));

const RadioIcon = styledDiv(({theme, selected}) => {
  const borderWidth = selected ? 3 : 1;
  return {
    height: theme.gutters.x12,
    width: theme.gutters.x12,
    border: `${borderWidth}px solid ${theme.colors.borderDeep}`,
    marginRight: theme.gutters.x8,
    borderRadius: '100%',
    backgroundColor: selected
      ? theme.colors.foreground
      : theme.colors.backgroundDeep,
  };
});

const TextCopyItem = styledDiv(
  ({theme, first, last, selected, hover, belowSelected}) => ({
    display: 'flex',
    alignItems: 'center',
    background: hover
      ? theme.colors.backgroundDeep
      : theme.colors.backgroundDeepAlt,
    marginTop: first ? theme.gutters.x8 : -1,
    height: theme.control.heightLarge,
    paddingLeft: theme.gutters.x8,
    paddingRight: theme.gutters.x8,
    borderTopRightRadius: first ? theme.control.borderRadius : 0,
    borderTopLeftRadius: first ? theme.control.borderRadius : 0,
    borderBottomRightRadius: last ? theme.control.borderRadius : 0,
    borderBottomLeftRadius: last ? theme.control.borderRadius : 0,
    borderWidth: theme.control.borderWidth,
    borderStyle: theme.control.borderStyle,
    borderTopColor:
      selected || belowSelected ? theme.colors.accent : theme.colors.borderDeep,
    borderBottomColor: selected ? theme.colors.accent : theme.colors.borderDeep,
    borderLeftColor: selected ? theme.colors.accent : theme.colors.borderDeep,
    borderRightColor: selected ? theme.colors.accent : theme.colors.borderDeep,
  })
);

const EditTextWrapper = styledDiv(({theme, width}) => ({
  background: theme.colors.background,
  border: `${theme.control.borderWidth}px solid ${theme.colors.borderDeep}`,
  borderRadius: theme.control.borderRadiusModal,
  width: width - theme.gutters.x8 * 2,
}));

const EditTextHeader = styledDiv(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${theme.gutters.x12 / 2}px ${theme.gutters.x8}px`,
  fontFamily: theme.typography.subheading.fontFamily,
  fontSize: theme.typography.subheading.fontSize,
  fontWeight: theme.typography.subheading.fontWeight,
  borderBottomStyle: 'solid',
  borderBottomWidth: theme.control.borderWidth,
  borderBottomColor: theme.colors.backgroundDeepAlt,
}));

const EditTextBody = emotionStyled('div')({
  padding: tokens.space[2],
});

const EditTextFooter = emotionStyled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  paddingLeft: tokens.space[2],
  paddingRight: tokens.space[2],
  paddingBottom: tokens.space[2],
});

const TextareaWrapper = styledDiv(
  ({theme}) => ({
    position: 'relative',
    background: theme.colors.backgroundDeep,
    borderWidth: theme.control.borderWidth,
    borderStyle: 'solid',
    borderColor: theme.colors.borderDeep,
    borderRadius: theme.control.borderRadius,
    overflow: 'hidden',
  }),
  {
    focus: ({theme: {focusColors, colors}}) => ({
      boxShadow: focusColors.outline,
      background: colors.borderDeep,
    }),
    hover: ({theme: {colors}}) => ({
      background: colors.borderDeep,
    }),
  }
);

const StyledTextarea = styled('textarea')(({theme}) => ({
  display: 'block',
  height: theme.gutters.x20 * 5 + theme.gutters.x12,
  width: '100%',
  resize: 'none',
  boxSizing: 'border-box',
  padding: theme.gutters.x4,
  fontSize: 12,
  fontFamily: 'inherit',
  color: 'inherit',
  background: 'transparent',
  outline: 0,
  borderWidth: 0,
  borderRadius: 0,
}));

const EditTextPanel = ({
  // @ts-expect-error - TS7031 - Binding element 'viewport' implicitly has an 'any' type.
  viewport,
  // @ts-expect-error - TS7031 - Binding element 'textCopy' implicitly has an 'any' type.
  textCopy,
  // @ts-expect-error - TS7031 - Binding element 'defaultTextCopy' implicitly has an 'any' type.
  defaultTextCopy,
  // @ts-expect-error - TS7031 - Binding element 'closePanel' implicitly has an 'any' type.
  closePanel,
  // @ts-expect-error - TS7031 - Binding element 'onSave' implicitly has an 'any' type.
  onSave,
  // @ts-expect-error - TS7031 - Binding element 'note' implicitly has an 'any' type.
  note,
}) => {
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);
  const [copy, setCopy] = React.useState(textCopy);

  const handleSaveWithClose = () => {
    onSave(copy);
    closePanel();
  };

  return (
    <EditTextWrapper width={viewport.width} onOutsideClick={closePanel}>
      <EditTextHeader>
        Edit Text
        <Button
          structure="ghost"
          aria-label="Close"
          onClick={closePanel}
          compact
          unitBorder
          style={{
            width: 20,
            height: 20,
          }}
        >
          <CloseDefaultIcon
            style={{
              display: 'flex',
            }}
          />
        </Button>
      </EditTextHeader>
      <EditTextBody>
        <TextareaWrapper
          hover={hover}
          focus={focus}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        >
          <StyledTextarea
            value={copy}
            // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
            onChange={(e) => setCopy(e.target.value)}
          />
        </TextareaWrapper>
      </EditTextBody>
      {note ? (
        <Note
          style={{marginInlineStart: 8, marginInlineEnd: 8, marginBlockEnd: 8}}
          colorVariant="default"
          hasIcon={false}
        >
          {note.copy}
          {note.cta ? (
            <NoteLink isExternal target="_blank" href={note.cta.link}>
              {note.cta.copy}
            </NoteLink>
          ) : null}
        </Note>
      ) : null}
      <EditTextFooter>
        {copy !== defaultTextCopy ? (
          <Button
            structure="ghost"
            onClick={() => setCopy(defaultTextCopy)}
            compact
            style={{
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            <ClockwiseIcon
              size={12}
              style={{
                display: 'flex',
                marginRight: 8,
              }}
            />
            Restore default
          </Button>
        ) : (
          <div />
        )}
        <Button
          disabled={copy === textCopy}
          onClick={handleSaveWithClose}
          compact
          style={{
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          Save
        </Button>
      </EditTextFooter>
    </EditTextWrapper>
  );
};

export const defaultMultiTextStateValue = {
  textStateCopy: {},
  temp: {
    state: '',
  },
} as const;

type State = {
  textStateCopy: Record<any, any>;
  temp: {
    state: string;
  };
};

const MultiTextEdit = ({
  state,
  dispatch,
  textStates,
  label,
  legacyStatePath,
  categorizedStates,
}: {
  state: State;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
  textStates: Record<any, any>;
  label: string;
  legacyStatePath?: Array<string>;
  categorizedStates?: Record<any, any>;
}) => {
  const allKeys = Object.keys(textStates);
  const textStateKeys = categorizedStates
    ? Object.keys(categorizedStates)
    : allKeys;
  const selectedTextStateId = state.temp
    ? state.temp.state
    : // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
      textStates[allKeys[0]].id;

  // This allows us to keep values that were set using legacy state paths (Commerce error messages)
  const textStateCopy = legacyStatePath
    ? Object.assign({}, get(state, legacyStatePath, {}), state.textStateCopy)
    : state.textStateCopy || {};

  const [hover, setHover] = React.useState<any>();

  const handleSaveMessage = (copy: any) => {
    const nextState = {
      ...state,
      textStateCopy: {
        ...state.textStateCopy,
        [selectedTextStateId as string]: copy,
      },
    } as const;
    dispatch(updateExpressionAction(createLiteralExpr(nextState)));
  };

  return (
    <div>
      {label}
      {textStateKeys.map((textStateKey, idx) => {
        const selected = selectedTextStateId === textStates[textStateKey].id;
        const selectedIdx = allKeys.findIndex(
          (key) => selectedTextStateId === textStates[key].id
        );
        const currentIdx = allKeys.findIndex((key) => key === textStateKey);
        const hovered = hover === textStateKey;
        // Fall back to the default constants if the error message doesn't exist on the node
        const textCopy = hover
          ? textStateCopy[textStates[hover].id] || textStates[textStateKey].copy
          : textStates[textStateKey].copy;

        return (
          <DeprecatedPopoverBridge
            key={textStateKey}
            renderPopoverContent={({closePopover, viewport}) => (
              <EditTextPanel
                viewport={viewport}
                textCopy={textCopy}
                defaultTextCopy={textStates[textStateKey].copy}
                closePanel={closePopover}
                onSave={handleSaveMessage}
                note={textStates[textStateKey].note}
              />
            )}
            showMask="TRANSPARENT"
            placement="bottom"
            showArrow={false}
            metricsIdentifier="multi-text-edit-popover"
          >
            {({Anchor, openPopover}) => (
              <Anchor asChild>
                <TextCopyItem
                  first={idx === 0}
                  last={idx === allKeys.length - 1}
                  selected={selected}
                  belowSelected={currentIdx === selectedIdx + 1 && idx !== 0}
                  hover={hovered}
                  onClick={() => {
                    const nextState = {
                      ...state,
                      temp: {
                        state: textStates[textStateKey].id,
                      },
                    } as const;
                    dispatch(
                      updateExpressionAction(createLiteralExpr(nextState))
                    );
                  }}
                  onMouseEnter={() => setHover(textStateKey)}
                  onMouseLeave={() => setHover(null)}
                >
                  <RadioIcon selected={selected} />
                  {textStates[textStateKey].name}

                  {hovered ? (
                    <FlexColumn alignItems="flex-end">
                      <Button
                        sizing="small"
                        icon
                        structure="ghost"
                        aria-label="Edit message"
                        onClick={openPopover}
                        style={{
                          alignSelf: 'unset',
                        }}
                      >
                        <EditIcon size={12} />
                      </Button>
                    </FlexColumn>
                  ) : null}
                </TextCopyItem>
              </Anchor>
            )}
          </DeprecatedPopoverBridge>
        );
      })}
    </div>
  );
};

export default MultiTextEdit;
